import {useParams} from "react-router";
import {useAutoAssignFormQuery} from "../components/use-assign-form-query";
import {useEffect, useState} from "react";
import ProjectUtils from "../../../../utils/project-utils";
import {navigateHook} from "../../../../hooks/use-navigate";
import {Template} from "../../../../components/template/template";
import {Card} from "../../../../components/card/card";
import InputForm from "../../../../components/input/input-form";
import InputDateTime from "../../../../components/input/input-datetime";
import Button from "../../../../components/button/button";
import Swal from "sweetalert2";
import SignedAlarmList from "../components/signed-alarm-list";
import autoAssignService from "../../../../services/web/auto-assign-service";
import projectUtils from "../../../../utils/project-utils.js";
import {FaPlus} from "react-icons/fa";

const AutoAssignForm = () => {
    const params = useParams();
    const navigate = navigateHook();

    const rawId = params?.id;
    const [currentId, setCurrentId] = useState( rawId && rawId !== "null" ? Number(rawId) : null);
    const { data: initialAssing, isLoading, refetch } = useAutoAssignFormQuery(currentId);

    const [plate, setPlate] = useState("");
    const [expireDate, setExpireDate] = useState(null);
    const [reason, setReason] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    const isEditMode = !!currentId;
    const loader = isLoading;

    useEffect(() => {
        if (!initialAssing) return;

        setPlate(initialAssing.plate);
        setReason(initialAssing.reason ?? "");
        
        if (initialAssing.expiresAt) {
            setExpireDate(new Date(initialAssing.expiresAt));
        } else {
            setExpireDate(null);
        }

    }, [initialAssing]);

    const validate = () => {
        if(!plate?.trim()) return 'O campo "Placa" é obrigatório.';
        if(!expireDate) return 'O campo "Data de expiração" é obrigatório.';
        if(expireDate.getTime() <= Date.now()) { return 'O campo "Data de expiração" não pode ser menor que o horário atual.' };

        return null;
    };
    
    const handleSave = async () => {
        if (isSaving) return;

        const err = validate();
        if(err) { ProjectUtils.swalErrorMessage('', err, 'Ok'); return; };

        const isoDate = expireDate.toISOString();

        const payloadCreate = {
            plate: plate.trim(), 
            expireDate: isoDate,
            reason: reason.trim(),
        };

        const payloadUpdate = {
            expireDate: isoDate,
            reason: reason.trim(),
        };

        const request = isEditMode
            ? autoAssignService.update(currentId, payloadUpdate)
            : autoAssignService.create(payloadCreate);
        
        try {
            setIsSaving(true);

            const data = await request;
            const newId = isEditMode ? currentId : (data?.id ?? data);

            setCurrentId(newId);
            projectUtils.success(!isEditMode);

            if (isEditMode) {
                await refetch();
            } else {
                const urlDev = `/auto-assign/form/${newId}`;
                navigate(urlDev, "");
            }
        }
        catch (error) {
            console.error(error);
            let message = "Não foi possível salvar.";

            if(error.response?.data) {
                message = error.response.data.message || error.response.data.error || message;
            }

            ProjectUtils.swalErrorMessage("", message, "Ok");
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = () => {
        if (!currentId) return;

        Swal.fire({
            title: "Excluir",
            text: `Excluir a placa "${plate}"?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#063A6C",
            confirmButtonText: "Sim",
            cancelButtonColor: "#c10007",
            cancelButtonText: "Não",
            reverseButtons: true,
        }).then((result) => {
            if (result.isConfirmed) {
                return autoAssignService
                     .remove(currentId)
                     .then(() => {
                        projectUtils.success(true, "Excluído com sucesso");
                        const urlDev = `/auto-assign`;
                        navigate(urlDev, "");
                     })
                      .catch((error) => {
                        console.error(error);
                        let message = "Não foi possível excluir.";

                        if (error.response?.status !== 500) {
                            message =
                                error.response?.data?.message || message;
                        }
                        ProjectUtils.swalErrorMessage("", message, "Ok");
                      });
            }
        });
    };

    const resetForm = () => {
        setPlate("");
        setExpireDate("");
        setReason("");
    };

    const newAssign = () => {
        resetForm();
        setCurrentId(null);

        const urlDev = "/auto-assign/form";
        navigate(urlDev);
    };

    return(
        <Template isLoading={loader || isSaving}>
            <Template.Header>
                <Template.Title>Assinatura automática de alarmes e eventos de integrações</Template.Title>
                {currentId && (
                    <Button onClick={newAssign} target="primary-outline"><FaPlus /> Adicionar placa</Button>
                )}
            </Template.Header>
            <Template.Body className="space-y-2 flex flex-col h-full min-h-0">
                <Card>
                    <Card.Body>
                        <div className="flex gap-4">
                            <InputForm
                                label="Placa *"
                                type="text"
                                classNameWrapper="flex-1"
                                value={plate}
                                onChange={(e) => setPlate(e.target.value)}
                                disabled={currentId}
                            />
                            <InputDateTime
                                id={"expireDate"}
                                label={"Data de expiração *"}
                                classNameWrapper="flex-2"
                                selected={expireDate}
                                dateFormat="LL"
                                onChange={(date) => setExpireDate(date || null)}
                            />
                            <InputForm
                                id="reason"
                                label={"Motivo"}
                                type={"text"}
                                classNameWrapper="flex-3"
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                            />
                        </div>
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Body>
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            {isEditMode && initialAssing && (
                                <div className="flex gap-2">
                                    {initialAssing.userName && initialAssing.createdAt && (
                                        <p className="content-center">
                                            Cadastrado por{" "}
                                            <b>{initialAssing.userName}</b> em {" "}
                                            <b>{ProjectUtils.formatDate(initialAssing.createdAt, "DD/MM/YYYY hh:mm")}</b>
                                        </p>
                                    )}
                                    {initialAssing.updatedByUserName && initialAssing.updatedOn && (
                                        <p className="content-center">
                                            Editado por{" "}
                                            <b>{initialAssing.updatedByUserName}</b> em {""}
                                            <b>{ProjectUtils.formatDate(initialAssing.updatedOn, "DD/MM/YYYY hh:mm")}</b>
                                        </p>
                                    )}
                                </div>
                            )}
                            <div className="flex gap-2 justify-end ml-auto">
                                {isEditMode && (
                                    <Button onClick={handleDelete} target="danger" actionFeedbackType='delete'>Excluir</Button>
                                )}
                                <Button onClick={handleSave} target="primary" disabled={isSaving} actionFeedbackType='save'>Salvar</Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
                {isEditMode && currentId && (
                    <Card className="flex-1 flex flex-col min-h-0">
                        <Card.Header>
                            <Card.Title>Eventos assinados</Card.Title>
                        </Card.Header>
                        <Card.Body className="flex">
                            <div className="h-[calc(100vh-247px)] w-full">
                                <SignedAlarmList autoAssignId={currentId} />
                            </div>
                        </Card.Body>
                    </Card>
                )}
            </Template.Body>
        </Template>
    )
}

export default AutoAssignForm;